import anime from "animejs";

const NON_STAGGERED_PART_KEYS = new Set([
    "star1",
    "star2",
    "star3",
    "star4",
    "bodyShadow1",
    "bodyShadow2",
    "mouth",
    "leftEye",
    "rightEye",
]);

export function initAnimations() {
    const logoRoot = document.querySelector(".patchbot-logo");

    if (!logoRoot) {
        return null;
    }

    return mountPatchBotLogoAnimation(logoRoot);
}

export function mountPatchBotLogoAnimation(rootElement, options = {}) {
    if (!rootElement) {
        return null;
    }

    const parts = buildParts(rootElement);

    if (Object.keys(parts).length === 0) {
        return null;
    }

    const shouldAnimate = options.shouldAnimate !== false;
    const enableReplayOnClick =
        shouldAnimate && options.enableReplayOnClick !== false;
    const allTargets = Object.values(parts).filter(Boolean);
    let activeAnimations = [];
    let activeRunId = 0;

    const stopAnimations = () => {
        activeRunId += 1;
        activeAnimations.forEach((animationInstance) => {
            animationInstance.pause();
        });
        activeAnimations = [];
        anime.remove(allTargets);
    };

    const runAnimation = () => {
        stopAnimations();

        if (!shouldAnimate) {
            revealAllParts(allTargets);
            return false;
        }

        hideAllParts(allTargets);
        options.onCycleStart?.();

        const currentRunId = activeRunId;

        activeAnimations = createLogoAnimationInstances(parts);

        Promise.all(
            activeAnimations.map((animationInstance) => {
                return animationInstance.finished.catch(() => null);
            })
        ).then(() => {
            if (currentRunId !== activeRunId) {
                return;
            }

            activeAnimations = [];
            revealAllParts(allTargets);
            options.onCycleComplete?.();
        });

        return true;
    };

    const handleClick = () => {
        runAnimation();
    };

    if (enableReplayOnClick) {
        rootElement.addEventListener("click", handleClick);
    }

    if (shouldAnimate && options.autoPlay !== false) {
        runAnimation();
    } else {
        revealAllParts(allTargets);
    }

    return {
        replay() {
            return runAnimation();
        },
        destroy() {
            if (enableReplayOnClick) {
                rootElement.removeEventListener("click", handleClick);
            }

            stopAnimations();
            revealAllParts(allTargets);
        },
    };
}

function createLogoAnimationInstances(parts) {
    const animationInstances = [];
    const bodyShadow = createAnimationInstance({
        targets: [parts.bodyShadow1],
        opacity: [0, 1],
        delay: 1700,
        easing: "easeInOutSine",
    });
    const bodyShadowSecondary = createAnimationInstance({
        targets: [parts.bodyShadow2],
        opacity: [0, 1],
        delay: 1500,
        easing: "easeInOutSine",
    });
    const faceFade = createAnimationInstance({
        targets: [parts.mouth, parts.rightEye, parts.leftEye],
        delay() {
            return anime.random(800, 1600);
        },
        opacity: [0, 1],
        easing: "easeInOutQuart",
    });
    const starFadeIn = createAnimationInstance({
        targets: [parts.star1, parts.star2, parts.star3, parts.star4],
        opacity: [0, 1],
        translateY: [-50, 0],
        delay() {
            return anime.random(0, 1000);
        },
        duration() {
            return anime.random(800, 1600);
        },
        easing: "easeInSine",
    });
    const fadedParts = getFadedParts(parts);

    [bodyShadow, bodyShadowSecondary, faceFade, starFadeIn]
        .filter(Boolean)
        .forEach((animationInstance) => {
            animationInstances.push(animationInstance);
        });

    if (fadedParts.length > 0) {
        const fadeInTimeline = anime.timeline();

        fadeInTimeline.add({
            targets: fadedParts,
            translateY: [50, 0],
            opacity: [0, 1],
            easing: "easeInOutQuart",
            duration: 2000,
            delay(_element, index) {
                return index * 80;
            },
        });

        animationInstances.push(fadeInTimeline);
    }

    return animationInstances;
}

function createAnimationInstance(config) {
    const targets = (
        Array.isArray(config.targets) ? config.targets : [config.targets]
    ).filter(Boolean);

    if (targets.length === 0) {
        return null;
    }

    return anime({
        ...config,
        targets,
    });
}

function getFadedParts(parts) {
    return Object.keys(parts).reduce((fadeInParts, key) => {
        if (!NON_STAGGERED_PART_KEYS.has(key) && parts[key]) {
            fadeInParts.push(parts[key]);
        }

        return fadeInParts;
    }, []);
}

function buildParts(rootElement) {
    return Array.from(rootElement.querySelectorAll("[id]")).reduce(
        (partMap, element) => {
            partMap[normalizePartName(element.id)] = element;
            return partMap;
        },
        {}
    );
}

function normalizePartName(str) {
    return str.replace(/(\_\w)/g, function (match) {
        return match[1].toUpperCase();
    });
}

function hideAllParts(targets) {
    targets.forEach((target) => {
        target.style.opacity = "0";
        target.style.transform = "";
    });
}

function revealAllParts(targets) {
    targets.forEach((target) => {
        target.style.opacity = "1";
        target.style.transform = "";
    });
}
